import { useDispatch, useSelector } from 'store/store'
import { Skeleton } from '@mui/material'
import { Input, SectionCard, SlimButton } from 'components'

import * as S from './styles'
import { Controller, useForm, useFormContext } from 'react-hook-form'
import { getUser } from 'commons/utils/user'
import { useParams } from 'react-router-dom'
import { OvMaintenanceBody } from 'store/OVContext/ovContextTypes'
import {
  setAlertJustification,
  setLoadingObservation,
  setNDJustificationDescription
} from 'store/OVContext/ovSlice'
import { OvMaintenanceResponse, TextObsMto } from 'services/manage_ov/interface'
import { oVMaintenance } from 'services/manage_ov'
import { toast } from 'react-toastify'
import { usePermissions } from 'commons/customHooks/useAuth'
import { ActionPerm } from 'components/Structures/Header/constants'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import { useMemo } from 'react'
import { OVTypes, YesNo } from 'constants/enums'

export const TextNFSection = () => {
  const { verifyPermission } = usePermissions()

  function shouldDisable(action: ActionPerm) {
    return !verifyPermission(SCREENS.OV, action)
  }

  const {
    ovSelected: {
      Cabecalho: { CodigoStatus, CodigoTipoOrdem }
    },
    observation,
    loadingObservation: loading,
    detailedInfo: { FaturamentoEntrega }
  } = useSelector((state) => state.ov)
  const { ovNumber } = useParams()

  const {
    control,
    reset,
    getValues,
    formState: { isDirty }
  } = useForm()
  const dispatch = useDispatch()
  const { getValues: contextGetValues } = useFormContext()

  const defaultValues = {
    TextosObservacao: observation?.TextosObservacao[0]?.DescricaoTexto || ''
  }

  const isYRBA = useMemo(() => CodigoTipoOrdem === OVTypes.YRBA, [])

  const editOv = async (ovID: string, requestBody: OvMaintenanceBody) => {
    try {
      dispatch(setLoadingObservation(true))
      const res: OvMaintenanceResponse = await oVMaintenance(ovID, requestBody)
      if (res.OperacaoRealizada) {
        toast.success('Ordem de Venda editada com sucesso.')
        if (isYRBA) dispatch(setAlertJustification(false))
        reset({
          ...getValues()
        })
      } else {
        toast.error(res?.Mensagem || `Não foi possível editar a Ordem de Venda.`)
      }
    } catch (error) {
      toast.error(`Erro ao editar a Ordem de Venda.`)
    } finally {
      if (isYRBA) {
        dispatch(setNDJustificationDescription(requestBody.Observacao![0].DescricaoTexto))
      }
      dispatch(setLoadingObservation(false))
    }
  }
  const handleSaveOV = () => {
    const data = contextGetValues()
    const desc = getValues('TextosObservacao')
    const Observacao = [...observation.TextosObservacao]
    const text = Observacao.shift()
    const obj = {
      ...text,
      DescricaoTexto: desc || ''
    }
    Observacao.unshift(obj as TextObsMto)
    const editOvRequestBody = {
      CodigoEntrega: data.cd_clie_entr,
      CodigoUsuarioAlteracao: getUser(),
      CondicaoPagamento: data.cd_cond_pgto,
      DiasAdicionais: +data.nr_dias_adic,
      NumeroOrdemVenda: ovNumber,
      NumeroPedidoCliente: data.nr_pedi_clie,
      TipoOrdem: data.cd_tipo_orde,
      ReplicaTextoNotaFiscal: isYRBA ? YesNo.YES : YesNo.NO,
      DataDesejadaCliente: FaturamentoEntrega.DataDesejadaCliente,
      DataPrevistaFaturamento: FaturamentoEntrega.DataPrevisaoFaturamento,
      Observacao
    }
    if (ovNumber) {
      editOv(ovNumber, editOvRequestBody as OvMaintenanceBody)
    }
  }
  return (
    <S.ColumnWrapper>
      <SectionCard
        title="Texto para nota fiscal"
        optional={!isYRBA}
        tooltip={!isYRBA}
        tooltipText={
          'Esta é uma seção opcional na qual você pode inserir um texto de até 255 caracteres para ser exibido na nota fiscal.'
        }
      >
        <S.Wrapper>
          <S.TextRow>
            <S.TextCol>
              <S.InfoItem>
                {loading ? (
                  <Skeleton height={110} />
                ) : (
                  <>
                    {isYRBA && (
                      <S.YrbaText>
                        <b>Atenção:</b> o conteúdo do texto para a nota fiscal (NF) será
                        replicado no campo “Descrição da solicitação” da negociação
                        diferenciada (ND), sobrescrevendo o conteúdo atual, caso exista.
                        Se necessário, será possível adicionar informações complementares
                        no campo de ND, mas elas não serão refletidas no texto da NF.
                      </S.YrbaText>
                    )}
                    <Controller
                      name={`TextosObservacao`}
                      control={control}
                      defaultValue={
                        observation?.TextosObservacao[0]?.DescricaoTexto || ''
                      }
                      render={({ field: { name, value, onChange } }) => (
                        <Input
                          name={name}
                          value={value}
                          onChange={onChange}
                          rows={4}
                          height={110}
                          label=""
                          placeholder="Insira aqui o texto para nota fiscal"
                          disabled={
                            +CodigoStatus !== 0 || shouldDisable(ACTIONS_SCREENS.SAVE)
                          }
                          showMaxLength
                          maxLength={255}
                        />
                      )}
                    />
                  </>
                )}
              </S.InfoItem>
            </S.TextCol>
          </S.TextRow>
          {isDirty && (
            <S.FormActions>
              <SlimButton
                content="Salvar"
                type="button"
                variant="primary"
                loading={loading}
                onClick={() => handleSaveOV()}
              />
              <SlimButton
                content="Desfazer"
                type="button"
                variant="secondary"
                disabled={loading}
                onClick={() => reset(defaultValues)}
              />
            </S.FormActions>
          )}
        </S.Wrapper>
      </SectionCard>
      {observation?.InformacaoMto && (
        <SectionCard
          title="MTO - Informação para expedição"
          optional
          tooltipText="Esta é uma seção opcional exibida devido à configuração específica de  pedidos que demanda um tipo de preparação especial do produto exigida pelo cliente. (Make To Order) Nela, você pode detalhar os dados necessários a serem exibidos nas caixas dos materiais ou dados de etiqueta."
        >
          <S.Wrapper>
            <S.TextRow>
              <S.TextCol>
                <S.InfoItem>
                  {loading ? (
                    <Skeleton height={110} />
                  ) : (
                    <Controller
                      name={`InformacaoMto`}
                      control={control}
                      defaultValue={observation?.InformacaoMto || ''}
                      render={({ field: { name, value, onChange } }) => (
                        <Input
                          name={name}
                          value={value}
                          onChange={onChange}
                          rows={4}
                          height={110}
                          label=""
                          placeholder="Insira aqui o texto de informação para expedição"
                          disabled
                          showMaxLength
                          maxLength={255}
                        />
                      )}
                    />
                  )}
                </S.InfoItem>
              </S.TextCol>
            </S.TextRow>
          </S.Wrapper>
        </SectionCard>
      )}
    </S.ColumnWrapper>
  )
}
